import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { reinitializeI18nOnOnline } from "../components/i18n";

/**
 * Hook to handle i18n refresh when translations are missing
 * @returns {Object} Functions to refresh i18n and check if refresh is needed
 */
export const useI18nRefresh = () => {
  const { t, i18n } = useTranslation();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Listen for i18n language changes to trigger re-renders
  useEffect(() => {
    const handleLanguageChange = () => {
      setRefreshTrigger((prev) => prev + 1);
    };

    i18n.on("languageChanged", handleLanguageChange);
    i18n.on("loaded", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
      i18n.off("loaded", handleLanguageChange);
    };
  }, [i18n]);

  const checkAndRefreshI18n = useCallback(async () => {
    // Check if we're online and if translations are missing
    if (!navigator.onLine) {
      return false;
    }

    // Test a few key translations including getMoodNames
    const testKeys = [
      "homepage.changeDate",
      "homepage.lastdays",
      "homepage.changeTheme",
      "settings.logout",
      "getMoodNames.flow",
      "getMoodNames.anxious",
      "getMoodNames.apathy",
    ];

    const missingTranslations = testKeys.some((key) => {
      const translation = t(key);
      // If translation equals the key, it means the translation is missing
      return translation === key;
    });

    if (missingTranslations) {
      console.log("🔄 Missing translations detected, refreshing i18n...");
      try {
        await reinitializeI18nOnOnline();
        setRefreshTrigger((prev) => prev + 1);
        return true;
      } catch (error) {
        console.error("❌ Failed to refresh i18n:", error);
        return false;
      }
    }

    return false;
  }, [t, i18n]);

  const forceRefreshI18n = useCallback(async () => {
    if (!navigator.onLine) {
      console.log("⚠️ Cannot refresh i18n while offline");
      return false;
    }

    try {
      await reinitializeI18nOnOnline();
      setRefreshTrigger((prev) => prev + 1);
      return true;
    } catch (error) {
      console.error("❌ Failed to force refresh i18n:", error);
      return false;
    }
  }, []);

  return {
    checkAndRefreshI18n,
    forceRefreshI18n,
    isOnline: navigator.onLine,
    refreshTrigger, // This can be used to trigger re-renders in components
  };
};

export default useI18nRefresh;
